/**
 * MapBox.jsx
 * Created by michaelbray on 2/23/18.
 */

import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import shallowEqual from 'fbjs/lib/shallowEqual';
import mapboxgl from 'mapbox-gl';

const TARGETED_ZOOM = 13;
const DEFAULT_ZOOM = 8;

const propTypes = {
    locationData: PropTypes.array,
    headerId: PropTypes.string,
    showOverlayImages: PropTypes.bool,
    mapBox: PropTypes.object
};

export class MapBox extends React.Component {
    /* eslint-disable no-undef */
    componentWillMount() {
        this.mapboxgl = mapboxgl;
    }
    /* eslint-enable no-undef */

    shouldComponentUpdate(nextProps) {
        if (!nextProps.locationData || nextProps.locationData.length === 0) {
            return false;
        }

        if (this.map !== undefined && this.props.locationData && shallowEqual(this.props.locationData, nextProps.locationData)) {
            return false;
        }

        return true;
    }

    componentDidUpdate() {
        const locatDat = this.props.locationData;
        if (locatDat !== undefined && locatDat.length > 0 && locatDat[0].addresses) {
            this.loadMap();
        }
    }

    getLocation() {
        let lat = 38.9072;
        let lng = -77.0369;
        let zoom = DEFAULT_ZOOM;
        if (this.props.locationData !== undefined && this.props.locationData.length > 0) {
            lat = this.props.locationData[0].facility_latitude;
            lng = this.props.locationData[0].facility_longitude;
            zoom = TARGETED_ZOOM;
        }
        return { lat, lng, zoom };
    }

    // Focuses search result when a marker is clicked on the map
    focusSearchResult(facilityId) {
        // Div of element to scroll to
        const divId = `${facilityId}_div`;
        const targetDiv = document.getElementById(divId);

        // Header element
        const headerDiv = document.getElementById(this.props.headerId);

        const yPos = this.findPos(targetDiv);
        const offset = headerDiv.offsetHeight;
        window.scroll(0, yPos - offset);
    }

    // Finds y coordinate of given object
    /* eslint-disable no-cond-assign */
    findPos(obj) {
        let givenObject = obj;
        let curtop = 0;
        if (givenObject !== null && givenObject.offsetParent) {
            do {
                curtop += givenObject.offsetTop;
            } while (givenObject = givenObject.offsetParent);
            return [curtop];
        }

        return 0;
    }
    /* eslint-enable no-cond-assign */

    prepareGeoJson() {
        const geoJSON = {
            type: "FeatureCollection",
            crs: {
                type: "name",
                properties: {
                    name: "urn:ogc:def:crs:OGC:1.3:CRS84"
                }
            },
            features: []
        };

        /* eslint-disable prefer-template */
        if (this.props.locationData) {
            this.props.locationData.forEach((location) => {
                let popupHTML = "";
                if (this.props.showOverlayImages) {
                    if (location.media && location.media.length > 0) {
                        popupHTML = '<img height="100" width="150" src="' + location.media[0].url + '" alt="' + location.media[0].title + '" />';
                    }
                }
                if (location.facility_id && location.facility_name) {
                    popupHTML = popupHTML.concat(
                        '<div className="text-inline m_T m_B-0 p_R-2x">' + location.facility_name + '</div>' +
                        '<Link id="info_redirect" to="/campgrounds/' + location.facility_id + '">' +
                        '<a href="#' + location.facility_id + '_div" className="text-inline m_T m_B-0 p_R-2x">Show campground details</a></Link><br/>' +
                        '<a id="campground_link">Go to campground page</a><br/>');
                }

                if (location.addresses && location.addresses.length > 0) {
                    popupHTML = popupHTML.concat('<span>Part of ' + location.addresses[0].city + ', ' + location.addresses[0].address_state_code + '</span>');
                }

                if (location.campsites_count) {
                    popupHTML = popupHTML.concat('<span> | ' + location.campsites_count + ' Available Sites</span>');
                }

                const feature = {
                    type: "Feature",
                    geometry: {
                        type: "Point",
                        coordinates: []
                    },
                    properties: {
                        description: popupHTML,
                        facilityId: location.facility_id
                    }
                };

                // adding location data to geoJSON data for markers
                feature.geometry.coordinates.push(location.facility_longitude);
                feature.geometry.coordinates.push(location.facility_latitude);
                // features.push(feature);
                geoJSON.features.push(feature);
            });
        }

        return geoJSON;
    }
    /* eslint-enable prefer-template */

    loadMap() {
        const geoJSON = this.prepareGeoJson();
        const self = this; // used for referring class inside callback functions
        const { lat, lng, zoom } = this.getLocation();
        this.mapboxgl.accessToken = process.env.MAPBOX_ACCESS_TOKEN;
        const map = new this.mapboxgl.Map({
            container: 'Mapbox',
            style: process.env.MAPBOX_STYLE,
            center: [lng, lat],
            zoom
        });

        // dont remove map var, required for shouldComponentUpdate() function
        this.map = map;

        map.on('load', () => {
            // Add a new source from our GeoJSON data and set the
            // 'cluster' option to true.
            map.addSource("projectzion", {
                type: "geojson",
                data: geoJSON
            });

            map.addLayer({
                id: "unclustered-points",
                type: "symbol",
                source: "projectzion",
                layout: {
                    "icon-image": "blue-triangle-15",
                    "icon-allow-overlap": true
                }
            });

            const bounds = new self.mapboxgl.LngLatBounds();

            geoJSON.features.forEach((feature) => {
                bounds.extend(feature.geometry.coordinates);
            });

            // maxzoom options ensures the map renders properly when only a single coord is set
            const options = {
                maxZoom: TARGETED_ZOOM
            };

            map.fitBounds(bounds, options);
            if (self.props.mapBox.disableScrollZooming) {
                map.scrollZoom.disable();
            }
        });

        // changes the cursor to a pointer to notify that the marker is clickable
        map.on('mousemove', (e) => {
            const features = map.queryRenderedFeatures(e.point, { layers: ['unclustered-points'] });
            map.getCanvas().style.cursor = (features.length) ? 'pointer' : '';
        });

        // opens popup when a marker is clicked, highlights and focuses search result
        map.on('click', (e) => {
            const features = map.queryRenderedFeatures(e.point, { layers: ['unclustered-points'] });
            if (!features.length) {
                return;
            }

            const feature = features[0];
            new self.mapboxgl.Popup()
                .setLngLat(feature.geometry.coordinates)
                .setHTML(feature.properties.description)
                .addTo(map);

            // centers map based on the clicked marker location
            map.flyTo({ center: feature.geometry.coordinates });

            if (self.props.highlightUpdate) {
                self.focusSearchResult(feature.properties.facilityId);
                self.props.highlightUpdate(feature.properties.facilityId);
            }
        });
    }

    render() {
        return (
            // keeping GMap and GMap-canvas css names for now until we fully migrate
            <div className="GMap">
                <div
                    id="Mapbox"
                    className="GMap-canvas"
                    ref={(div) => {
                        this.mapCanvas = div;
                    }} />
            </div>
        );
    }
}

MapBox.propTypes = propTypes;

function select(state) {
    return {
        mapBox: state.mapBox
    };
}

export default connect(select)(MapBox);
